import {
  type DefinedInitialDataInfiniteOptions,
  type InfiniteData,
  type QueryKey,
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type DateRange,
  type EventFilters,
  type Media,
  MediaType,
  type MediaView,
  type PageableContent,
  type RequestFilters,
  type UUID,
  type UserFavoriteMediaRequest,
} from '@cobliteam/common-types';

import { FootageRequester, GraphQLCameraMediaRequester, SharedMediaRequester, UserFavoriteMediaRequester } from '@cobliteam/api-lib';
import { MediaRequestStatus as MediaRequestStatusV2 } from '@cobliteam/api-lib/src/__generated__/graphql';

import { MediaTableType } from '@cobliteam/media';
import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const cameraMediaGraphQLRequester = new GraphQLCameraMediaRequester();
const sharedMediaRequester = new SharedMediaRequester({ host: env.apiHost });
const footageRequester = new FootageRequester({ host: env.apiHost });
const userFavoriteMediaRequester = new UserFavoriteMediaRequester({
  host: env.apiHost,
});
const QUERY_KEY: QueryKey = ['CAMERA-MEDIA'];

const useGetMediaView = (mediaId?: UUID, shouldGenerateDescription?: Boolean) => {
  const queryClient = useQueryClient();

  return useQuery<MediaView>({
    queryKey: [...QUERY_KEY, 'VIEW', mediaId],
    queryFn: async () => {
      const media = await cameraMediaGraphQLRequester.getMediaView(`${mediaId}`, shouldGenerateDescription || false);
      if (media) {
        queryClient.setQueriesData(
          {
            queryKey: [...QUERY_KEY, media.mediaInfo.mediaType === MediaType.EVENT_VIDEO ? 'LIST-EVENT' : 'LIST-REQUEST'],
          },
          (old: InfiniteData<PageableContent<Media>> | undefined) => {
            if (!old) return old;
            const updatedPages = old.pages.map((item) => {
              return {
                ...item,
                content: item.content.map((mediaItem: Media) => {
                  if (mediaItem.id === mediaId) {
                    return {
                      ...mediaItem,
                      mediaViewCount: mediaItem.mediaViewCount + 1,
                    };
                  }
                  return mediaItem;
                }),
              };
            });
            return {
              ...old,
              pages: updatedPages,
            };
          }
        );
      }
      return media;
    },
  });
};

const useGetMediaEventCount = (vehicleId: string, period: DateRange) => {
  return useQuery<number>({
    queryKey: [`${QUERY_KEY}-media-count`, vehicleId, period],
    queryFn: () => cameraMediaGraphQLRequester.getMediaEventCount(vehicleId, period),
    refetchOnMount: false,
  });
};

const useGetMediaRequestList = (filters: RequestFilters, options?: Partial<UseQueryOptions<PageableContent<Media>>>) => {
  return useQuery<PageableContent<Media>>({
    queryKey: [...QUERY_KEY, 'LIST', filters],
    queryFn: () => cameraMediaGraphQLRequester.getMediaRequestList(filters),
    staleTime: 300000,
    ...options,
  });
};

const useGetMediaEventList = (filters: EventFilters, options?: Partial<UseQueryOptions<PageableContent<Media>>>) => {
  return useQuery<PageableContent<Media>>({
    queryKey: [...QUERY_KEY, 'LIST', filters],
    queryFn: () => cameraMediaGraphQLRequester.getMediaEventList(filters),
    staleTime: 300000,
    ...options,
  });
};

const useGetMediaRequestInfinity = (filters: RequestFilters, options?: Partial<DefinedInitialDataInfiniteOptions<PageableContent<Media>>>) => {
  return useInfiniteQuery<PageableContent<Media>>({
    queryKey: [...QUERY_KEY, 'LIST-REQUEST', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => cameraMediaGraphQLRequester.getMediaRequestList(filters, pageParam),
    staleTime: 300000,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
    ...options,
  });
};

const useGetMediaEventInfinity = (filters: EventFilters, options?: Partial<DefinedInitialDataInfiniteOptions<PageableContent<Media>>>) => {
  return useInfiniteQuery<PageableContent<Media>>({
    queryKey: [...QUERY_KEY, 'LIST-EVENT', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => cameraMediaGraphQLRequester.getMediaEventList(filters, pageParam),
    staleTime: 300000,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
    ...options,
  });
};

const useCreateMediaRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cameraMediaGraphQLRequester.createMedia,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, 'LIST-REQUEST'],
      });
    },
  });
};

const useCreateMediaShareLink = () => {
  return useMutation({
    mutationFn: sharedMediaRequester.shareMedia,
  });
};

const useUpdateFavoriteMedia = (page: MediaTableType) => {
  const queryClient = useQueryClient();

  const queryKeyPage = page === MediaTableType.EVENT_MEDIA ? 'LIST-EVENT' : 'LIST-REQUEST';
  return useMutation({
    mutationFn: ({
      updateUserFavoriteMedia,
    }: {
      updateUserFavoriteMedia: UserFavoriteMediaRequest;
    }) => userFavoriteMediaRequester.updateUserFavoriteMedia(updateUserFavoriteMedia),
    onMutate: async (request) => {
      const previousList = await queryClient.getQueriesData({
        queryKey: [...QUERY_KEY, queryKeyPage],
      });
      const previousView = await queryClient.getQueriesData({
        queryKey: [...QUERY_KEY, 'VIEW', request.updateUserFavoriteMedia.mediaId],
      });

      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, 'VIEW', request.updateUserFavoriteMedia.mediaId],
        },
        (old: MediaView | undefined) => {
          if (!old) return old;
          return {
            ...old,
            mediaInfo: {
              ...old.mediaInfo,
              isFavorite: request.updateUserFavoriteMedia.isFavorite,
            },
          };
        }
      );

      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, queryKeyPage],
        },
        (old: InfiniteData<PageableContent<Media>> | undefined) => {
          if (!old) return old;
          const updatedPages = old.pages.map((item) => {
            return {
              ...item,
              content: item.content.map((media: Media) => {
                if (media.id === request.updateUserFavoriteMedia.mediaId) {
                  return {
                    ...media,
                    isFavorite: request.updateUserFavoriteMedia.isFavorite,
                  };
                }
                return media;
              }),
            };
          });
          return {
            ...old,
            pages: updatedPages,
          };
        }
      );

      return { previousList, previousView };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, queryKeyPage, { favoriteMedia: true }],
      });
    },
    onError: (err, request, context: any) => {
      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, queryKeyPage],
        },
        context.previousList
      );
      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, 'VIEW', request.updateUserFavoriteMedia.mediaId],
        },
        context.previousView
      );
    },
  });
};

const useRetryFootageRequest = () => {
  const queryClient = useQueryClient();
  const listQueryKey = 'LIST-REQUEST';

  return useMutation<number, unknown, { mediaId: UUID }>({
    mutationFn: (request) => footageRequester.retryFootageRequest(request.mediaId),
    onMutate: async (request) => {
      const previousList = await queryClient.getQueriesData({
        queryKey: [...QUERY_KEY, listQueryKey],
      });

      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, listQueryKey],
        },
        (old: InfiniteData<PageableContent<Media>> | undefined) => {
          if (!old) return old;
          const updatedPages = old.pages.map((item) => {
            return {
              ...item,
              content: item.content.map((media: Media) => {
                if (media.id === request.mediaId) {
                  return {
                    ...media,
                    mediaDetails: {
                      ...media.mediaDetails,
                      updatedAt: new Date().getTime(),
                      requestStatus: MediaRequestStatusV2.ManualRetryRequest,
                    },
                  };
                }
                return media;
              }),
            };
          });
          return {
            ...old,
            pages: updatedPages,
          };
        }
      );

      return { previousList };
    },
    onError: (err, _, context: any) => {
      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, listQueryKey],
        },
        context.previousList
      );
    },
  });
};

export {
  useCreateMediaRequest,
  useCreateMediaShareLink,
  useGetMediaEventInfinity,
  useGetMediaRequestInfinity,
  useGetMediaView,
  useUpdateFavoriteMedia,
  useGetMediaRequestList,
  useGetMediaEventList,
  useGetMediaEventCount,
  useRetryFootageRequest,
};
